import React, { useEffect, useState, forwardRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Button,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Fade,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router";
import {
  getJobApplications,
  updateApplicationStatus,
} from "../../services/applicationService";
import { getResumeById } from "../../services/resumeService";
import PageContainer from "./PageContainer";
import { useAuth } from "../../auth/AuthContext";

const STATUS_COLORS = {
  APPLIED: "info",
  UNDER_REVIEW: "warning",
  SELECTED: "success",
  REJECTED: "error",
};

// Transition
const Transition = forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} {...props} timeout={300} />;
});

// Styled frosted-glass dialog
const BlurredDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
    border: `2px solid ${theme.palette.primary.main}`,
    background: (theme.vars || theme).palette.background.paper,
    backdropFilter: "blur(8px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    padding: theme.spacing(3),
  },
}));

export default function JobApplications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResume, setSelectedResume] = useState(null);

  const handleShortlistClick = (JobTitle) => {
    navigate(`/app/hr/applications/job/${id}/applications/shortlist` , {state: { JobTitle } });
  };

useEffect(() => {
  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await getJobApplications(id);
      if (response.success) {
        setApplications(response.data);
      }
    } catch (err) {
      console.error("Error fetching job applications:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchApplications();
}, [id]);

  const handleStatusChange = async (appId, newStatus) => {
    try {
      const response = await updateApplicationStatus(appId, newStatus);
      if (response.success) {
        setApplications((prev) =>
          prev.map((a) =>
            a.id === appId ? { ...a, status: response.data.status } : a
          )
        );
      }
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const handleViewResume = async (jobId) => {
    try {
      const response = await getResumeById(jobId);
      if (response.success) {
        setSelectedResume(response.message.parsedJson);
      }
    } catch (err) {
      console.error("Error fetching resume:", err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (applications.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="text.secondary">
          No applications found for this job.
        </Typography>
      </Box>
    );
  }

  return (
    <PageContainer
      title={`Applications for ${applications[0].job.jobTitle}`}
      breadcrumbs={[
        { title: 'Applications', path: '/app/hr/applications' },
        { title: applications[0].job.jobTitle },
      ]}
      actions={[
        <Stack direction="row" alignItems="center" spacing={1}>
          {user?.role === "HR" && (
            <Button
              variant="contained"
              onClick={() => handleShortlistClick(applications[0].job.jobTitle)}
              startIcon={<ListItemIcon />}
            >
              Shortlist Resume
            </Button>
          )}
        </Stack>
        // <Button
        //   key="shortlist"
        //   variant="contained"
        //   color="primary"
        //   onClick={() => getMatchingResumes(applications[0].job.id)}
        // >
        //   Shortlist Resumes
        // </Button>
      ]}
    >
      <Stack spacing={2}>
        {applications.map((app) => (
          <Card key={app.id} sx={{ borderRadius: 1, boxShadow: 2 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="h6">{app.resume.parsedJson.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {app.resume.parsedJson.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Applied on: {new Date(app.appliedAt).toLocaleString()}
                  </Typography>
                </Box>
                <Chip
                  label={app.status}
                  color={STATUS_COLORS[app.status] || "default"}
                  variant="outlined"
                />
              </Box>

              <Box mt={2} display="flex" gap={2}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleViewResume(app.resume.id)}
                >
                  View Resume
                </Button>
                <Select
                  size="small"
                  value={app.status}
                  onChange={(e) => handleStatusChange(app.id, e.target.value)}
                >
                  {Object.keys(STATUS_COLORS).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Resume Dialog */}
      {/* Resume Dialog */}
      <BlurredDialog
        open={!!selectedResume}
        onClose={() => setSelectedResume(null)}
        maxWidth="md"
        fullWidth
        slots={{ transition: Transition }}
      >
        {selectedResume && (
          <>
            <DialogTitle>
              <Typography variant="h5" fontWeight="bold">
                {selectedResume.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedResume.location || "N/A"}
              </Typography>
            </DialogTitle>

            <DialogContent dividers>
              {/* Contact Info */}
              <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
                <Typography variant="body2">📧 {selectedResume.email}</Typography>
                <Typography variant="body2">📱 {selectedResume.phone || "N/A"}</Typography>
                {selectedResume.linkedin && (
                  <Typography variant="body2">
                    🔗 <a href={`https://${selectedResume.linkedin}`} target="_blank" rel="noreferrer">{selectedResume.linkedin}</a>
                  </Typography>
                )}
                {selectedResume.github && (
                  <Typography variant="body2">
                    💻 <a href={`https://${selectedResume.github}`} target="_blank" rel="noreferrer">{selectedResume.github}</a>
                  </Typography>
                )}
              </Stack>

              {/* Summary */}
              {selectedResume.summary && (
                <>
                  <Typography variant="h6" gutterBottom>Summary</Typography>
                  <Typography variant="body2" paragraph>{selectedResume.summary}</Typography>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Experience */}
              {selectedResume.experience?.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Experience</Typography>
                  <List dense>
                    {selectedResume.experience.map((exp, i) => (
                      <ListItem key={i} sx={{ pl: 0 }}>
                        <ListItemText
                          primary={`${exp.role} @ ${exp.company}`}
                          secondary={`${exp.startDate} - ${exp.endDate} • ${exp.description}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Education */}
              {selectedResume.education?.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Education</Typography>
                  <List dense>
                    {selectedResume.education.map((edu, i) => (
                      <ListItem key={i} sx={{ pl: 0 }}>
                        <ListItemText
                          primary={`${edu.degree} - ${edu.institution}`}
                          secondary={`${edu.startDate} - ${edu.endDate} | ${edu.fieldOfStudy}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Projects */}
              {selectedResume.projects?.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Projects</Typography>
                  <List dense>
                    {selectedResume.projects.map((proj, i) => (
                      <ListItem key={i} sx={{ pl: 0 }}>
                        <ListItemText
                          primary={proj.name}
                          secondary={proj.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Skills */}
              {selectedResume.skills?.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Skills</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    {selectedResume.skills.map((skill, i) => (
                      <Chip key={i} label={skill} variant="outlined" size="small" />
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Certifications */}
              {selectedResume.certifications?.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Certifications</Typography>
                  <List dense>
                    {selectedResume.certifications.map((cert, i) => (
                      <ListItem key={i} sx={{ pl: 0 }}>
                        <ListItemText primary={cert} />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Languages */}
              {selectedResume.languages?.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Languages</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selectedResume.languages.map((lang, i) => (
                      <Chip key={i} label={lang} color="primary" size="small" />
                    ))}
                  </Stack>
                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setSelectedResume(null)} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </BlurredDialog>
    </PageContainer>
  );
}
